<template>
  <div v-if="completion" class="completion-panel" role="dialog" aria-modal="true" @click.self="$emit('close')">
    <div class="completion-dialog">
      <div class="completion-header">
        <div>
          <h6 class="mb-1">{{ $t('profileCompletion.title') }}</h6>
          <p class="text-muted small mb-0">{{ $t('profileCompletion.panelSubtitle') }}</p>
        </div>
        <button type="button" class="btn-close" :aria-label="$t('common.close')" @click="$emit('close')"></button>
      </div>

      <div class="completion-body">
        <div class="d-flex justify-content-between align-items-end mb-1">
          <span class="small text-muted">{{ $t('profileCompletion.overall') }}</span>
          <span class="fs-4 fw-bold" :class="completion.isComplete ? 'text-success' : 'text-danger'">
            {{ completion.completionPercentage }}%
          </span>
        </div>

        <div class="progress mb-3" style="height: 8px;">
          <div
            class="progress-bar"
            :class="completion.isComplete ? 'bg-success' : 'bg-danger'"
            :style="{ width: `${completion.completionPercentage}%` }"
          ></div>
        </div>

        <div v-if="!completion.isComplete" class="alert alert-danger py-2 px-3 small">
          <i class="ph-duotone ph-warning-circle me-1"></i>{{ $t('profileCompletion.visibilityMessage') }}
        </div>

        <!--
          Required first and labelled as such: those are what the warning is about, and
          mixing them with suggestions would make neither group actionable.
        -->
        <template v-if="requiredFields.length">
          <div class="group-label">
            {{ $t('profileCompletion.missingRequired') }}
            <span class="badge bg-danger-subtle text-danger-emphasis ms-1">{{ missingRequiredCount }}</span>
          </div>
          <ul class="field-list">
            <li v-for="field in requiredFields" :key="field.key">
              <button
                type="button"
                class="field-row"
                :class="{ 'is-done': field.isFilled }"
                :disabled="field.isFilled"
                @click="goToField(field)"
              >
                <i :class="field.isFilled ? 'ph-duotone ph-check-circle text-success' : 'ph-duotone ph-circle text-danger'"></i>
                <span class="flex-grow-1">{{ fieldLabel(field.key) }}</span>
                <span v-if="!field.isFilled" class="action-hint">
                  {{ $t('profileCompletion.goFill') }}<i class="ph-duotone ph-arrow-right ms-1"></i>
                </span>
              </button>
            </li>
          </ul>
        </template>

        <template v-if="optionalFields.length">
          <div class="group-label mt-3">{{ $t('profileCompletion.missingOptional') }}</div>
          <ul class="field-list">
            <li v-for="field in optionalFields" :key="field.key">
              <button
                type="button"
                class="field-row"
                :class="{ 'is-done': field.isFilled }"
                :disabled="field.isFilled"
                @click="goToField(field)"
              >
                <i :class="field.isFilled ? 'ph-duotone ph-check-circle text-success' : 'ph-duotone ph-circle text-muted'"></i>
                <span class="flex-grow-1">{{ fieldLabel(field.key) }}</span>
                <span v-if="!field.isFilled" class="action-hint">
                  {{ $t('profileCompletion.goFill') }}<i class="ph-duotone ph-arrow-right ms-1"></i>
                </span>
              </button>
            </li>
          </ul>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

const props = defineProps({
  completion: { type: Object, default: null }
})

const emit = defineEmits(['close'])

const { t } = useI18n()
const router = useRouter()

/** Field keys come from the server; labels are resolved here so they stay translated. */
const fieldLabel = (key) => t(`profileCompletion.fields.${key}`, key)

/**
 * Where a field is edited.
 *
 * The profile page is not one surface: contact details live on its Account tab, while
 * the address sits in the Company card on the Overview tab. Sending everything to
 * "/profile" dropped the user on the Overview tab with the right card still collapsed,
 * which is why the link looked like it went nowhere. The focus query tells the page
 * which tab to open and which card to expand.
 */
const SECTION_ROUTES = {
  company: { path: '/profile', query: { focus: 'company' } },
  contact: { path: '/profile', query: { focus: 'account' } },
  categories: { path: '/profile/categories' },
  ports: { path: '/profile/ports' },
  certificates: { path: '/profile/certificates' }
}

// Company-tab fields differ in where they are edited, so a few override their section.
const FIELD_ROUTES = {
  companyName: { path: '/profile', query: { focus: 'account' } },
  displayName: { path: '/profile', query: { focus: 'account' } },
  // The About card is always expanded, so the Overview tab alone is enough.
  about: { path: '/profile' },
  // The logo is not in the Company card: it is uploaded via the avatar overlay in the
  // profile sidebar, which sits outside the tabs.
  logo: { path: '/profile', query: { focus: 'logo' } },
  address: { path: '/profile', query: { focus: 'company' } },
  country: { path: '/profile', query: { focus: 'company' } }
}

const fieldRoute = (field) =>
  FIELD_ROUTES[field.key] || SECTION_ROUTES[field.section] || { path: '/profile' }

/**
 * Navigate first, then close. Closing on click unmounted the row before the router had
 * finished, which is why "Go fill" appeared to do nothing.
 */
const goToField = async (field) => {
  if (field.isFilled) return

  const target = fieldRoute(field)

  // Vue Router skips a push to the identical location, so clicking the same row twice
  // would not re-trigger the page's focus handling. A changing nonce keeps each click a
  // real navigation.
  await router.push({
    ...target,
    query: { ...(target.query || {}), t: Date.now() }
  })

  emit('close')
}

// Missing entries lead, so the work to do is at the top; filled ones stay visible as
// ticked rows, which is what makes the list read as progress.
const byMissingFirst = (a, b) => Number(a.isFilled) - Number(b.isFilled)

const requiredFields = computed(() =>
  (props.completion?.fields || []).filter(f => f.isRequired).sort(byMissingFirst)
)

const optionalFields = computed(() =>
  (props.completion?.fields || []).filter(f => !f.isRequired).sort(byMissingFirst)
)

const missingRequiredCount = computed(() => requiredFields.value.filter(f => !f.isFilled).length)
</script>

<style scoped>
.completion-panel {
  position: fixed;
  inset: 0;
  z-index: 1080;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
}

.completion-dialog {
  background: #fff;
  border-radius: 12px;
  width: 100%;
  max-width: 520px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.completion-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--bs-border-color);
  flex-shrink: 0;
}

/* Only the list scrolls; the score stays in view while working down it. */
.completion-body {
  padding: 1.25rem;
  overflow-y: auto;
}

.group-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  color: var(--bs-secondary-color);
  margin-bottom: 0.5rem;
}

.field-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

/* Rendered as buttons for keyboard access, so the default chrome is reset. */
.field-row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  width: 100%;
  padding: 0.55rem 0.75rem;
  border: 0;
  border-radius: 8px;
  background: none;
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;
}

/* Only rows that go somewhere respond to the pointer; filled ones are disabled. */
.field-row:not(:disabled):hover {
  background: var(--bs-tertiary-bg, #f1f3f5);
}

.field-row:disabled,
.field-row.is-done {
  color: var(--bs-secondary-color);
  cursor: default;
}

.action-hint {
  font-size: 0.75rem;
  color: var(--bs-primary);
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.15s;
}

.field-row:not(:disabled):hover .action-hint {
  opacity: 1;
}

/* Touch devices have no hover, so the affordance must always be visible there. */
@media (hover: none) {
  .action-hint {
    opacity: 1;
  }
}
</style>
